import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import {
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  LayoutGrid,
  Maximize2,
  Minimize2,
  Plus,
  Search,
  Upload,
  User,
  X,
} from 'lucide-react'
import {
  startOfWeek,
  endOfWeek,
  format,
  parseISO,
  addDays,
  addWeeks,
  addMonths,
} from 'date-fns'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getDateRange, type ShiftViewMode } from '../utils/dateRange'

import { PageShell } from '@/components/common/PageShell'
import { PermissionGate } from '@/components/common/PermissionGate'
import {
  Button,
  Card,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { ShiftTemplateBones } from './ShiftTemplateBones'
import { usePermissions } from '@/hooks/usePermissions'
import { useFullscreenShortcut } from '@/hooks/useFullscreenShortcut'
import { isTypingTarget } from '@/lib/keyboard'
import { useCompanies, useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { useMe } from '@/features/settings/hooks/useMe'
import { useDeleteShift, useShiftAssignment } from '../hooks/useShifts'
import {
  useShiftsAcrossCompanies,
  useShiftAssignmentsAcrossCompanies,
  useShiftsRoster,
} from '../hooks/useShiftsAcrossCompanies'
import { ShiftPlanningGrid } from './ShiftPlanningGrid'
import type { PlanningGridUser, PlanningGridCompanyGroup } from './ShiftPlanningGrid'
import { CreateShiftModal } from './CreateShiftModal'
import { EditShiftModal } from './EditShiftModal'
import { CreateAssignmentModal } from './CreateAssignmentModal'
import { EditAssignmentModal } from './EditAssignmentModal'
import { CancelAssignmentModal } from './CancelAssignmentModal'
import { BulkAssignModal } from './BulkAssignModal'
import { UploadRotaModal } from './UploadRotaModal'
import { CompanyChips } from './CompanyChips'
import { ShiftTemplateCard } from './ShiftTemplateCard'
import { ShiftTemplateCarousel } from './ShiftTemplateCarousel'
import { getCompanyColor } from '../utils/companyColor'
import type { Shift, ShiftAssignment, ShiftAssignmentUserRef } from '@/types/shift.types'
import { cn } from '@/lib/cn'

type ViewMode = ShiftViewMode

function parseCompanyParam(value: string | null): string | null {
  if (!value) return null
  return (
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .at(0) ?? null
  )
}

function buildShiftPageSearchParams(input: {
  companyId: string | null
  status?: string
  search?: string
}): URLSearchParams {
  const params = new URLSearchParams()
  if (input.companyId) params.set('companyId', input.companyId)
  if (input.status) params.set('status', input.status)
  if (input.search) params.set('search', input.search)
  return params
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'Status: All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function ShiftManagementPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const hasShiftsRead = can('shifts', 'read')

  const canReadAllCompanies = can('companies', 'read')
  const myCompaniesQuery = useMyCompanies()
  const allCompaniesQuery = useCompanies(undefined, { enabled: canReadAllCompanies })
  const companiesLoading = canReadAllCompanies
    ? allCompaniesQuery.isLoading
    : myCompaniesQuery.isLoading
  const myCompanies = useMemo(() => {
    if (canReadAllCompanies) {
      return (allCompaniesQuery.data ?? []).map((c) => ({
        _id: c._id,
        name: c.name,
        slug: c.slug,
        status: c.status,
      }))
    }
    return myCompaniesQuery.data ?? []
  }, [canReadAllCompanies, allCompaniesQuery.data, myCompaniesQuery.data])
  const { data: me } = useMe()

  /** Selected company chip filter. Empty set means "all". */
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(
    () => {
      const companyId = parseCompanyParam(searchParams.get('companyId'))
      return companyId ? new Set([companyId]) : new Set()
    }
  )
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') ?? '')
  const [search, setSearch] = useState<string>(() => searchParams.get('search') ?? '')
  const [viewMode, setViewMode] = useState<ViewMode>('monthly')
  const [focusedDate, setFocusedDate] = useState<Date>(() => new Date())
  const [groupBy, setGroupBy] = useState<'company' | 'person'>('company')
  const [showFilters, setShowFilters] = useState(false)
  const [showViews, setShowViews] = useState(false)
  const [showCreateActions, setShowCreateActions] = useState(false)
  const toolbarMenusRef = useRef<HTMLElement>(null)
  // Full-screen planning grid — all shifts on one max-width surface.
  // F toggles, Esc exits, matching the WallBoard full-screen view.
  const [isGridFullscreen, setIsGridFullscreen] = useState(false)

  useEffect(() => {
    if (!showFilters && !showViews && !showCreateActions) return

    const closeMenus = (): void => {
      setShowFilters(false)
      setShowViews(false)
      setShowCreateActions(false)
    }
    const handlePointerDown = (event: MouseEvent): void => {
      if (toolbarMenusRef.current?.contains(event.target as Node)) return
      closeMenus()
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeMenus()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showFilters, showViews, showCreateActions])

  // Modal state
  const [createShiftOpen, setCreateShiftOpen] = useState<{ companyId: string } | null>(null)
  const [bulkAssignOpen, setBulkAssignOpen] = useState<{ companyId?: string } | null>(null)
  const [rotaUploadOpen, setRotaUploadOpen] = useState<{ companyId?: string } | null>(null)
  const [editShift, setEditShift] = useState<{ companyId: string; shiftId: string } | null>(
    null
  )
  const [shiftToDelete, setShiftToDelete] = useState<
    { companyId: string; shift: Shift } | null
  >(null)
  const [createAssignmentPrefill, setCreateAssignmentPrefill] = useState<{
    companyId: string
    companyOptions: Array<{ companyId: string; companyName: string }>
    userId: string
    date: string
  } | null>(null)
  const [editAssignment, setEditAssignment] = useState<
    { companyId: string; assignmentId: string } | null
  >(null)
  // Deep-link from a notification: ?companyId=…&shiftAssignmentId=… focuses the grid on that
  // shift's date and briefly highlights it (instead of opening the edit modal).
  const deepLinkParamId = searchParams.get('shiftAssignmentId')
  const [highlightAssignmentId, setHighlightAssignmentId] = useState<string | undefined>(
    deepLinkParamId ?? undefined
  )
  const [highlightCompanyId, setHighlightCompanyId] = useState<string | null>(() =>
    parseCompanyParam(searchParams.get('companyId'))
  )
  // Re-arm when the param changes — including clicking another notification while already on
  // this page (no remount). Set-state-during-render so it runs even though the URL-sync effect
  // strips shiftAssignmentId; lastSeenDeepLinkId tracks edges (incl. transitions back to null,
  // so re-clicking the same notification re-arms).
  const [lastSeenDeepLinkId, setLastSeenDeepLinkId] = useState<string | null>(deepLinkParamId)
  if (deepLinkParamId !== lastSeenDeepLinkId) {
    setLastSeenDeepLinkId(deepLinkParamId)
    if (deepLinkParamId) {
      setHighlightAssignmentId(deepLinkParamId)
      setHighlightCompanyId(parseCompanyParam(searchParams.get('companyId')))
    }
  }
  // Resolve the assignment by id (works even when it's outside the loaded range) so we can
  // jump the grid to its date.
  const { data: deepLinkAssignment } = useShiftAssignment(
    highlightAssignmentId ? highlightCompanyId : null,
    highlightAssignmentId ?? null
  )
  // Jump the grid to the deep-linked shift's date once it resolves — set-state-during-render,
  // the recommended alternative to an effect for adjusting state from changed data.
  const [focusedDeepLinkId, setFocusedDeepLinkId] = useState<string | null>(null)
  if (deepLinkAssignment && focusedDeepLinkId !== deepLinkAssignment._id) {
    setFocusedDeepLinkId(deepLinkAssignment._id)
    setFocusedDate(parseISO(deepLinkAssignment.date))
  }
  // Fade the highlight out a few seconds after the shift has loaded into the grid.
  useEffect(() => {
    if (!highlightAssignmentId || !deepLinkAssignment) return
    const t = setTimeout(() => setHighlightAssignmentId(undefined), 5000)
    return () => clearTimeout(t)
  }, [highlightAssignmentId, deepLinkAssignment])
  const [cancelAssignment, setCancelAssignment] = useState<
    { companyId: string; assignmentId: string } | null
  >(null)

  const visibleCompanies = useMemo(
    () =>
      selectedCompanyIds.size === 0
        ? myCompanies
        : myCompanies.filter((c) => selectedCompanyIds.has(c._id)),
    [myCompanies, selectedCompanyIds]
  )
  const visibleCompanyIds = useMemo(
    () => visibleCompanies.map((c) => c._id),
    [visibleCompanies]
  )

  const companyNameById = useMemo(() => {
    const m = new Map<string, string>()
    myCompanies.forEach((c) => m.set(c._id, c.name))
    return m
  }, [myCompanies])

  const { startDate, endDate } = useMemo(
    () => getDateRange(viewMode, focusedDate),
    [viewMode, focusedDate]
  )

  // Every template across the visible companies (fetched page by page).
  const { shifts: allShifts, isLoading: shiftsLoading } =
    useShiftsAcrossCompanies(visibleCompanyIds)

  // Debounce search to keep keystrokes from spamming the backend.
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') ?? '')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  const fullscreenShortcutBlocked =
    createShiftOpen !== null ||
    bulkAssignOpen !== null ||
    rotaUploadOpen !== null ||
    editShift !== null ||
    shiftToDelete !== null ||
    createAssignmentPrefill !== null ||
    editAssignment !== null ||
    cancelAssignment !== null

  useFullscreenShortcut({
    isFullscreen: isGridFullscreen,
    setIsFullscreen: setIsGridFullscreen,
    disabled: fullscreenShortcutBlocked,
  })

  const openUpcomingShifts = useCallback(() => {
    const qs = buildShiftPageSearchParams({
      companyId: selectedCompanyIds.values().next().value ?? null,
      status: statusFilter || undefined,
      search: search.trim() || undefined,
    }).toString()
    navigate(qs ? `/shifts/upcoming?${qs}` : '/shifts/upcoming')
  }, [navigate, search, selectedCompanyIds, statusFilter])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target)) return
      if (event.key.toLowerCase() !== 'u') return
      if (fullscreenShortcutBlocked || showFilters || showViews || showCreateActions) return

      event.preventDefault()
      openUpcomingShifts()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [fullscreenShortcutBlocked, openUpcomingShifts, showCreateActions, showFilters, showViews])

  // NOTE: the search box searches team members by name (see usersForGrid, which
  // filters rows by u.name). It is deliberately NOT forwarded to the assignment
  // query: the backend `search` filters assignments by shift/slot name only
  // (never the employee's name), so sending a person's name here matched zero
  // slots and hid every shift for staff whose name differs from their slot name
  // (e.g. "jawad" vs a slot named "TAHIR 19:00-07:00"). Row filtering happens
  // client-side; each visible row still pulls only its own assignments.
  const assignmentParams = useMemo(
    () => ({
      startDate,
      endDate,
      limit: 100,
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [startDate, endDate, statusFilter]
  )

  useEffect(() => {
    const next = buildShiftPageSearchParams({
      companyId: selectedCompanyIds.values().next().value ?? null,
      status: statusFilter || undefined,
      search: debouncedSearch || undefined,
    })
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    debouncedSearch,
    searchParams,
    selectedCompanyIds,
    setSearchParams,
    statusFilter,
  ])

  // Admin path: roster (members + assignments in one call) so staff with no shifts
  // in the range still appear as empty rows. Self-service path stays on /me.
  const rosterMode = useShiftsRoster(
    hasShiftsRead ? visibleCompanyIds : [],
    assignmentParams
  )
  const mineMode = useShiftAssignmentsAcrossCompanies(
    hasShiftsRead ? [] : visibleCompanyIds,
    assignmentParams,
    { mineOnly: true, keepPrevious: true }
  )
  const allAssignments = useMemo(
    () => (hasShiftsRead ? rosterMode.assignments : mineMode.assignments),
    [hasShiftsRead, rosterMode.assignments, mineMode.assignments]
  )
  const rosterMembers = useMemo(
    () => (hasShiftsRead ? rosterMode.members : []),
    [hasShiftsRead, rosterMode.members]
  )
  const assignmentsLoading = hasShiftsRead ? rosterMode.isLoading : mineMode.isLoading

  // Scroll the highlighted shift into view once the grid has rendered it (re-runs as the grid
  // data lands for the newly focused date).
  useEffect(() => {
    if (!highlightAssignmentId) return
    document
      .getElementById(`shift-assignment-${highlightAssignmentId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightAssignmentId, allAssignments])

  // Derive grid rows. Admin path seeds from roster members (so staff without shifts
  // in the range still get an empty row); self-service falls back to assignment-derived.
  const usersForGrid = useMemo<PlanningGridUser[]>(() => {
    const seen = new Set<string>()
    const out: PlanningGridUser[] = []

    rosterMembers.forEach((m) => {
      const key = `${m.companyId}:${m.userId}`
      if (seen.has(key)) return
      seen.add(key)
      const name =
        [m.firstName, m.lastName].filter(Boolean).join(' ').trim() ||
        m.email ||
        m.userId
      out.push({
        _id: m.userId,
        name,
        companyId: m.companyId,
        companyName: companyNameById.get(m.companyId),
        companyTags: companyNameById.has(m.companyId)
          ? [{ companyId: m.companyId, companyName: companyNameById.get(m.companyId)! }]
          : [],
        roleId: m.roleId,
        roleName: m.roleName,
        roleSlug: m.roleSlug,
        roleNames: m.roleName ? [m.roleName] : [],
      })
    })

    allAssignments.forEach((a) => {
      const uid =
        typeof a.userId === 'object' && a.userId !== null
          ? (a.userId as ShiftAssignmentUserRef)._id
          : (a.userId as string)
      const key = `${a.companyId}:${uid}`
      if (seen.has(key)) return
      seen.add(key)
      const ref = typeof a.userId === 'object' ? (a.userId as ShiftAssignmentUserRef) : null
      const name = ref
        ? [ref.firstName, ref.lastName].filter(Boolean).join(' ').trim() ||
          ref.email ||
          uid
        : uid
      out.push({
        _id: uid,
        name,
        companyId: a.companyId,
        companyName: companyNameById.get(a.companyId),
        companyTags: a.companyId && companyNameById.has(a.companyId)
          ? [{ companyId: a.companyId, companyName: companyNameById.get(a.companyId)! }]
          : [],
      })
    })

    // For self-service mode (no shifts:read), inject the current user even if no assignments yet
    if (!hasShiftsRead && me) {
      visibleCompanies.forEach((c) => {
        const key = `${c._id}:${me._id}`
        if (!seen.has(key)) {
          seen.add(key)
          out.push({
            _id: me._id,
            name: [me.firstName, me.lastName].filter(Boolean).join(' ').trim() || me.email,
            companyId: c._id,
            companyName: c.name,
            companyTags: [{ companyId: c._id, companyName: c.name }],
          })
        }
      })
    }

    let result = out
    if (search.trim()) {
      const q = search.toLowerCase().trim()
      result = result.filter((u) => u.name.toLowerCase().includes(q))
    }
    if (groupBy === 'person') {
      // Collapse to one row per user (drop companyId so the grid uses flat-mode lookup)
      const byUser = new Map<
        string,
        PlanningGridUser & {
          companyTagMap: Map<string, string>
          roleNameSet: Set<string>
        }
      >()
      result.forEach((u) => {
        const existing = byUser.get(u._id)
        if (existing) {
          u.companyTags?.forEach((tag) => existing.companyTagMap.set(tag.companyId, tag.companyName))
          if (u.roleName) existing.roleNameSet.add(u.roleName)
          return
        }
        const companyTagMap = new Map<string, string>()
        u.companyTags?.forEach((tag) => companyTagMap.set(tag.companyId, tag.companyName))
        byUser.set(u._id, {
          _id: u._id,
          name: u.name,
          companyTags: u.companyTags ?? [],
          companyTagMap,
          roleId: u.roleId,
          roleName: u.roleName,
          roleSlug: u.roleSlug,
          roleNames: u.roleName ? [u.roleName] : [],
          roleNameSet: new Set(u.roleName ? [u.roleName] : []),
        })
      })
      return Array.from(byUser.values()).map(({ companyTagMap, roleNameSet, ...user }) => ({
        ...user,
        companyTags: Array.from(companyTagMap, ([companyId, companyName]) => ({
          companyId,
          companyName,
        })),
        roleNames: Array.from(roleNameSet),
      }))
    }
    return result
  }, [
    rosterMembers,
    allAssignments,
    companyNameById,
    hasShiftsRead,
    me,
    search,
    visibleCompanies,
    groupBy,
  ])

  const companyGroups = useMemo<PlanningGridCompanyGroup[]>(
    () =>
      groupBy === 'company'
        ? visibleCompanies.map((c) => ({ companyId: c._id, companyName: c.name }))
        : [],
    [visibleCompanies, groupBy]
  )

  const templatesByCompany = useMemo(() => {
    const map = new Map<string, Shift[]>()
    allShifts.forEach((s) => {
      if (!map.has(s.companyId)) map.set(s.companyId, [])
      map.get(s.companyId)!.push(s)
    })
    return map
  }, [allShifts])

  // Search is now applied server-side via assignmentParams.search; assignments arrive pre-filtered.
  const filteredAssignments = allAssignments

  // Stable so the memoised grid skips re-rendering when only a menu or modal changes.
  const handleGridAssign = useCallback(
    (
      userId: string,
      date: string,
      companyId?: string,
      companyOptions: Array<{ companyId: string; companyName: string }> = []
    ) => {
      if (date < format(new Date(), 'yyyy-MM-dd')) return
      const eligibleCompanies = companyOptions.filter((company) =>
        visibleCompanyIds.includes(company.companyId)
      )
      const resolvedCompanyId =
        companyId ?? (eligibleCompanies.length === 1 ? eligibleCompanies[0].companyId : '')
      setCreateAssignmentPrefill({
        companyId: resolvedCompanyId,
        companyOptions: eligibleCompanies,
        userId,
        date,
      })
    },
    [visibleCompanyIds]
  )
  const handleGridEdit = useCallback(
    (a: ShiftAssignment) => {
      const cid = (a as { companyId?: string }).companyId ?? visibleCompanies[0]?._id ?? ''
      setEditAssignment({ companyId: cid, assignmentId: a._id })
    },
    [visibleCompanies]
  )
  const handleGridCancel = useCallback(
    (a: ShiftAssignment) => {
      const cid = (a as { companyId?: string }).companyId ?? visibleCompanies[0]?._id ?? ''
      setCancelAssignment({ companyId: cid, assignmentId: a._id })
    },
    [visibleCompanies]
  )

  // A month of rows takes a few hundred ms to draw. Deferring it lets a click
  // (People/Company, next month, a company chip) paint at once while the new
  // grid renders in the background, with the old one dimmed until it is ready.
  const gridData = useMemo(
    () => ({
      viewMode,
      startDate,
      endDate,
      users: usersForGrid,
      assignments: filteredAssignments,
      shifts: allShifts,
      companyGroups,
      highlightAssignmentId,
    }),
    [
      viewMode,
      startDate,
      endDate,
      usersForGrid,
      filteredAssignments,
      allShifts,
      companyGroups,
      highlightAssignmentId,
    ]
  )
  // While the next period loads, the query hands back the last one; keep the
  // grid on the last complete period rather than new dates over old shifts.
  const showingPreviousPeriod = hasShiftsRead
    ? rosterMode.isPlaceholderData
    : mineMode.isPlaceholderData
  const [settledGridData, setSettledGridData] = useState(gridData)
  if (!showingPreviousPeriod && settledGridData !== gridData) setSettledGridData(gridData)
  const deferredGridData = useDeferredValue(showingPreviousPeriod ? settledGridData : gridData)
  const gridIsStale = deferredGridData !== gridData

  const deleteShift = useDeleteShift()

  const handleConfirmDelete = useCallback(() => {
    if (shiftToDelete) {
      deleteShift.mutate(
        {
          companyId: shiftToDelete.companyId,
          id: shiftToDelete.shift._id,
        },
        {
          onSuccess: () => setShiftToDelete(null),
        }
      )
    }
  }, [shiftToDelete, deleteShift])

  const toggleCompany = useCallback(
    (companyId: string | null) => {
      if (companyId === null) {
        setSelectedCompanyIds(new Set())
        return
      }
      setSelectedCompanyIds((prev) =>
        prev.has(companyId) ? new Set() : new Set([companyId])
      )
    },
    []
  )

  const handlePrev = useCallback(() => {
    if (viewMode === 'daily') setFocusedDate((d) => addDays(d, -1))
    else if (viewMode === 'weekly') setFocusedDate((d) => addWeeks(d, -1))
    else setFocusedDate((d) => addMonths(d, -1))
  }, [viewMode])

  const handleNext = useCallback(() => {
    if (viewMode === 'daily') setFocusedDate((d) => addDays(d, 1))
    else if (viewMode === 'weekly') setFocusedDate((d) => addWeeks(d, 1))
    else setFocusedDate((d) => addMonths(d, 1))
  }, [viewMode])

  const rangeLabel =
    viewMode === 'daily'
      ? format(focusedDate, 'PPP')
      : viewMode === 'weekly'
        ? `${format(startOfWeek(focusedDate, { weekStartsOn: 1 }), 'd MMM')} – ${format(endOfWeek(focusedDate, { weekStartsOn: 1 }), 'd MMM yyyy')}`
        : format(focusedDate, 'MMMM yyyy')

  const breadcrumbItems = [
    { label: 'Time & Attendance', to: '/dashboard' },
    { label: 'Shifts' },
  ]

  if (companiesLoading) {
    return (
      <PageShell breadcrumbItems={breadcrumbItems} icon={Calendar} title="Shifts">
        <div className="space-y-6">
          <ShiftTemplateBones />
          <Card className="overflow-hidden rounded-xl" contentClassName="p-0">
            <SectionLoader variant="grid" label="Loading schedule" />
          </Card>
        </div>
      </PageShell>
    )
  }

  if (myCompanies.length === 0) {
    return (
      <PageShell breadcrumbItems={breadcrumbItems} icon={Calendar} title="Shifts">
        <Card>
          <p className="py-8 text-center text-sm text-gray-600 dark:text-gray-400">
            You are not a member of any company yet. Shifts are scoped to companies.
          </p>
        </Card>
      </PageShell>
    )
  }

  // The scheduling grid, shared by the normal page and the full-screen view.
  const planningGrid =
    assignmentsLoading ? (
      <SectionLoader variant="grid" label="Loading schedule" />
    ) : (
      <div
        aria-busy={gridIsStale}
        className={cn('transition-opacity', gridIsStale && 'opacity-60 delay-150')}
      >
        <ShiftPlanningGrid
          {...deferredGridData}
          onAssign={handleGridAssign}
          onEdit={handleGridEdit}
          onCancel={handleGridCancel}
          myAssignmentsOnly={!hasShiftsRead}
        />
      </div>
    )

  return (
    <PageShell
      breadcrumbItems={breadcrumbItems}
      icon={Calendar}
      title="Shifts"
      description="Plan coverage, manage shift templates, and assign teams across your companies."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <div className="-mt-4 space-y-4">
          <section
            ref={toolbarMenusRef}
            className="relative -mx-4 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6"
          >
          {/* Wraps rather than scrolls: a sideways-scrolling toolbar shows a
              full-width scrollbar on Windows and clips the menus it opens. */}
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
            <div className="inline-flex h-9 shrink-0 items-center overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
              <button
                type="button"
                onClick={handlePrev}
                className="flex h-full w-8 items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Previous range"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[165px] border-x border-gray-300 px-3 text-center text-sm font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-300">
                {rangeLabel}
              </span>
              <button
                type="button"
                onClick={handleNext}
                className="flex h-full w-8 items-center justify-center text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Next range"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="min-w-[220px] flex-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search staff, shift names, emails or notes..."
                  aria-label="Search shifts"
                  className={cn(
                    'h-9 pl-9 pr-9',
                    debouncedSearch && 'border-primary/40 ring-2 ring-primary/10'
                  )}
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    aria-label="Clear shift search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              {debouncedSearch ? (
                <p className="mt-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                  Showing {usersForGrid.length}{' '}
                  {usersForGrid.length === 1 ? 'result' : 'results'} for "{debouncedSearch}"
                </p>
              ) : null}
            </div>

            <div className="shrink-0">
              <SelectRoot
                value={viewMode}
                onValueChange={(value) => setViewMode(value as ViewMode)}
              >
                <SelectTrigger
                  aria-label="Schedule view"
                  className="h-9 w-28 gap-1.5 rounded-lg border-gray-300 pl-2.5 pr-2 text-xs font-semibold text-gray-700 dark:text-gray-200"
                >
                  <CalendarDays className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                  <SelectValue placeholder="View" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Day</SelectItem>
                  <SelectItem value="weekly">Week</SelectItem>
                  <SelectItem value="monthly">Month</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>

            <div className="relative shrink-0">
              <Button
                variant="secondary"
                size="sm"
                className={cn('gap-1.5', showFilters && 'border-primary/30 bg-primary/10 text-primary')}
                onClick={() => {
                  setShowFilters((current) => !current)
                  setShowViews(false)
                  setShowCreateActions(false)
                }}
                aria-expanded={showFilters}
              >
                <Filter className="h-3.5 w-3.5" aria-hidden />
                Filter
              </Button>
            </div>

            <div className="relative shrink-0">
              <Button
                variant="secondary"
                size="sm"
                className={cn('gap-1.5', showViews && 'border-primary/30 bg-primary/10 text-primary')}
                onClick={() => {
                  setShowViews((current) => !current)
                  setShowFilters(false)
                  setShowCreateActions(false)
                }}
                aria-expanded={showViews}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                {groupBy === 'person' ? 'People' : 'Company'}
              </Button>
            </div>
            <div className="shrink-0">
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  setShowFilters(false)
                  setShowViews(false)
                  setShowCreateActions(false)
                  openUpcomingShifts()
                }}
                title="View upcoming assigned shifts (press U)"
                aria-keyshortcuts="U"
              >
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                Upcoming
                <kbd className="ml-1 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  U
                </kbd>
              </Button>
            </div>
            <PermissionGate module="shifts" action="bulk_assign">
              <div className="shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    setRotaUploadOpen({})
                    setShowFilters(false)
                    setShowViews(false)
                    setShowCreateActions(false)
                  }}
                >
                  <Upload className="h-3.5 w-3.5" aria-hidden />
                  Upload ROTA
                </Button>
              </div>
            </PermissionGate>
            <PermissionGate module="shifts" action="create">
              <div className="relative flex shrink-0">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    setCreateShiftOpen({ companyId: visibleCompanies[0]?._id ?? '' })
                  }
                  className="gap-1.5 rounded-r-none border-r border-white/25 bg-primary shadow-sm hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4" />
                  Create shift
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateActions((open) => !open)
                    setShowFilters(false)
                    setShowViews(false)
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-r-lg bg-primary text-white shadow-sm transition hover:bg-primary/90"
                  aria-label="More shift actions"
                  aria-expanded={showCreateActions}
                >
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                </button>
                {showCreateActions ? (
                  <div className="absolute right-0 top-10 z-40 w-44 rounded-xl border border-gray-200 bg-white p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.16)] dark:border-gray-700 dark:bg-gray-800">
                    <PermissionGate module="shifts" action="bulk_assign">
                      <button type="button" onClick={() => { setShowCreateActions(false); setBulkAssignOpen({}) }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
                        <Briefcase className="h-4 w-4" /> Assign to staff
                      </button>
                    </PermissionGate>
                    <PermissionGate module="shifts" action="bulk_assign">
                      <button type="button" onClick={() => { setShowCreateActions(false); setRotaUploadOpen({}) }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
                        <Upload className="h-4 w-4" /> Upload ROTA
                      </button>
                    </PermissionGate>
                  </div>
                ) : null}
              </div>
            </PermissionGate>
          </div>
          {showFilters ? (
            <div className="absolute right-44 top-[58px] z-40 w-[360px] rounded-xl border border-gray-200 bg-white p-3 shadow-[0_18px_45px_rgba(15,23,42,0.16)] dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-2 text-xs font-semibold text-gray-900 dark:text-gray-100">Filter schedule</p>
              <div className="space-y-2">
                <Select options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter} clearable={false} className="w-full" />
                <div className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                  <CompanyChips companies={myCompanies} selectedCompanyIds={selectedCompanyIds} onToggle={toggleCompany} />
                </div>
              </div>
            </div>
          ) : null}
          {showViews ? (
            <div className="absolute right-24 top-[58px] z-40 w-52 rounded-xl border border-gray-200 bg-white p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.16)] dark:border-gray-700 dark:bg-gray-800">
              {([
                { value: 'person' as const, label: 'People', description: 'One row per team member', icon: User },
                { value: 'company' as const, label: 'Company', description: 'Group members by company', icon: Building2 },
              ]).map((option) => {
                const Icon = option.icon
                const selected = groupBy === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setGroupBy(option.value)
                      setShowViews(false)
                    }}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                      selected ? 'bg-primary/10 text-primary dark:bg-primary-light/15' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      <span className="block text-xs font-semibold">{option.label}</span>
                      <span className="block text-[10px] text-gray-500 dark:text-gray-400">{option.description}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          ) : null}
          </section>

          {/* Templates remain in the normal page flow and scroll away with the page. */}
          <PermissionGate module="shifts" action="read">
            <section>
            <div className="mb-2.5 flex items-center gap-2 px-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Shift templates
              </span>
              {shiftsLoading && allShifts.length > 0 && <Spinner size="sm" aria-label="Refreshing shift templates" />}
            </div>
            {visibleCompanies.length === 0 ? null : allShifts.length === 0 && shiftsLoading ? (
              <ShiftTemplateBones />
            ) : allShifts.length === 0 ? (
              <Card>
                <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No shift templates yet.{' '}
                  <PermissionGate module="shifts" action="create">
                    <button
                      type="button"
                      className="font-medium text-primary hover:underline"
                      onClick={() =>
                        setCreateShiftOpen({ companyId: visibleCompanies[0]?._id ?? '' })
                      }
                    >
                      Create one
                    </button>
                  </PermissionGate>
                  .
                </p>
              </Card>
            ) : (
              <ShiftTemplateCarousel>
                {visibleCompanies.flatMap((c) => {
                  const templates = templatesByCompany.get(c._id) ?? []
                  const color = getCompanyColor(c._id)
                  const cards = templates.map((t) => (
                    <div
                      key={t._id}
                      className="w-56 shrink-0 snap-start sm:w-60"
                    >
                      <ShiftTemplateCard
                        shift={t}
                        companyName={c.name}
                        companyId={c._id}
                        color={color}
                        canUpdate={can('shifts', 'update')}
                        canDelete={can('shifts', 'delete')}
                        onEdit={() => setEditShift({ companyId: c._id, shiftId: t._id })}
                        onDelete={() => setShiftToDelete({ companyId: c._id, shift: t })}
                      />
                    </div>
                  ))
                  return cards
                })}
              </ShiftTemplateCarousel>
            )}
            </section>
          </PermissionGate>
        </div>

        {/* Planning grid */}
        <Card className="overflow-visible rounded-xl" contentClassName="p-0">
          <div className="flex items-center justify-between gap-3 rounded-t-xl border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {viewMode === 'daily' ? 'Daily schedule' : viewMode === 'monthly' ? 'Monthly schedule' : 'Weekly schedule'}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              onClick={() => setIsGridFullscreen(true)}
              title="Open the schedule full screen (press F)"
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden />
              Full screen
              <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                F
              </kbd>
            </Button>
          </div>
          <div className="min-w-0 w-full overflow-hidden">{planningGrid}</div>
        </Card>

        {/* Full-screen schedule — every shift on one max-width surface. */}
        {isGridFullscreen ? (
          <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-950" data-fit-root>
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-3 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-primary" aria-hidden />
                <h1 className="text-lg font-semibold tracking-tight text-gray-950 dark:text-gray-100">
                  Schedule
                </h1>
                <span className="hidden text-sm text-gray-500 dark:text-gray-400 sm:inline">
                  {rangeLabel}
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={() => setIsGridFullscreen(false)}
                title="Exit full screen (press F or Esc)"
              >
                <Minimize2 className="h-3.5 w-3.5" aria-hidden />
                Exit
                <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  F
                </kbd>
                <kbd className="ml-1.5 hidden rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-gray-500 sm:inline-block dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Esc
                </kbd>
              </Button>
            </div>
            <div className="min-w-0 flex-1 overflow-auto p-4">{planningGrid}</div>
          </div>
        ) : null}

        <BulkAssignModal
          companies={visibleCompanies.length > 0 ? visibleCompanies : myCompanies}
          defaultCompanyId={bulkAssignOpen?.companyId}
          open={bulkAssignOpen !== null}
          onClose={() => setBulkAssignOpen(null)}
          startDate={startDate}
          endDate={endDate}
        />
        <UploadRotaModal
          companies={visibleCompanies.length > 0 ? visibleCompanies : myCompanies}
          defaultCompanyId={rotaUploadOpen?.companyId}
          open={rotaUploadOpen !== null}
          onClose={() => setRotaUploadOpen(null)}
          startDate={startDate}
          endDate={endDate}
        />
        {createShiftOpen && createShiftOpen.companyId && (
          <CreateShiftModal
            companyId={createShiftOpen.companyId}
            open={createShiftOpen !== null}
            onClose={() => setCreateShiftOpen(null)}
          />
        )}
        {editShift && (
          <EditShiftModal
            companyId={editShift.companyId}
            shiftId={editShift.shiftId}
            open={editShift !== null}
            onClose={() => setEditShift(null)}
          />
        )}
        {createAssignmentPrefill && (
          <CreateAssignmentModal
            companyId={createAssignmentPrefill.companyId}
            companyOptions={createAssignmentPrefill.companyOptions}
            open={createAssignmentPrefill !== null}
            onClose={() => setCreateAssignmentPrefill(null)}
            prefill={{
              userId: createAssignmentPrefill.userId,
              date: createAssignmentPrefill.date,
            }}
          />
        )}
        {editAssignment && (
          <EditAssignmentModal
            companyId={editAssignment.companyId}
            assignmentId={editAssignment.assignmentId}
            onClose={() => setEditAssignment(null)}
            onCancel={(a) => {
              setEditAssignment(null)
              setCancelAssignment({
                companyId: editAssignment.companyId,
                assignmentId: a._id,
              })
            }}
          />
        )}
        {cancelAssignment && (
          <CancelAssignmentModal
            companyId={cancelAssignment.companyId}
            assignmentId={cancelAssignment.assignmentId}
            onClose={() => setCancelAssignment(null)}
            onSuccess={() => setEditAssignment(null)}
          />
        )}
        <ConfirmDialog
          open={shiftToDelete !== null}
          onClose={() => setShiftToDelete(null)}
          onConfirm={handleConfirmDelete}
          title="Delete shift"
          message={
            shiftToDelete
              ? `Delete shift "${shiftToDelete.shift.name}"? This cannot be undone and may affect existing assignments.`
              : ''
          }
          confirmLabel="Delete"
          variant="danger"
          confirmLoading={deleteShift.isPending}
        />
      </div>
    </PageShell>
  )
}

ShiftManagementPage.displayName = 'ShiftManagementPage'
